import axios, { AxiosInstance } from 'axios';
import { adminApiUrl } from '../config/api';

export interface AdminLoginCredentials {
  email: string;
  password: string;
}

export interface AdminUser {
  adminId: string;
  email: string;
  name: string;
  role: 'SUPER_ADMIN' | 'SUPPORT_ADMIN' | 'CONTENT_ADMIN';
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  lastLoginAt?: string;
}

export interface AdminLoginResponse {
  admin: AdminUser;
  token: string;
}

const TOKEN_STORAGE_KEY = 'admin_token';
const ADMIN_STORAGE_KEY = 'admin_user';

class AdminAuthService {
  private api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL: adminApiUrl,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.api.interceptors.request.use((config) => {
      const token = this.getToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });

    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401 || error.response?.status === 403) {
          this.logout();
        }
        return Promise.reject(error);
      }
    );
  }

  async login(credentials: AdminLoginCredentials): Promise<AdminLoginResponse> {
    let response;
    try {
      response = await this.api.post<{
        success: boolean;
        data?: {
          admin: AdminUser;
          accessToken: string;
          idToken: string;
          refreshToken: string;
          expiresIn: number;
        };
        error?: { code?: string; message?: string };
      }>('/admin/login', credentials);
    } catch (err: unknown) {
      const axiosErr = err as {
        response?: { data?: { error?: { message?: string } }; status?: number };
      };
      const msg = axiosErr.response?.data?.error?.message;
      if (axiosErr.response?.status === 401 && msg) {
        throw new Error(msg);
      }
      if (axiosErr.response?.status === 401) {
        throw new Error(
          'Invalid email or password, or no admin user exists. Create one: npm run create-admin-user (see README).'
        );
      }
      throw err;
    }

    if (!response.data.success || !response.data.data) {
      throw new Error('Login failed');
    }

    const { admin, accessToken } = response.data.data;
    // Use accessToken for admin API endpoints (GetUserCommand requires AccessToken)
    // idToken is for API Gateway Cognito authorizers, but our admin endpoints use GetUserCommand
    this.setToken(accessToken);
    this.setAdmin(admin);

    return { admin, token: accessToken };
  }

  async getCurrentAdmin(): Promise<AdminUser> {
    const response = await this.api.get<{ success: boolean; data: { admin: AdminUser } }>(
      '/admin/me'
    );

    if (!response.data.success || !response.data.data?.admin) {
      throw new Error('Failed to get admin info');
    }

    const admin = response.data.data.admin;
    this.setAdmin(admin);
    return admin;
  }

  logout(): void {
    localStorage.removeItem(TOKEN_STORAGE_KEY);
    localStorage.removeItem(ADMIN_STORAGE_KEY);
  }

  getToken(): string | null {
    return localStorage.getItem(TOKEN_STORAGE_KEY);
  }

  setToken(token: string): void {
    localStorage.setItem(TOKEN_STORAGE_KEY, token);
  }

  getAdmin(): AdminUser | null {
    const adminStr = localStorage.getItem(ADMIN_STORAGE_KEY);
    if (!adminStr) return null;
    try {
      return JSON.parse(adminStr);
    } catch {
      return null;
    }
  }

  setAdmin(admin: AdminUser): void {
    localStorage.setItem(ADMIN_STORAGE_KEY, JSON.stringify(admin));
  }

  isAuthenticated(): boolean {
    return !!this.getToken();
  }
}

export const adminAuthService = new AdminAuthService();

